import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkRehype from 'remark-rehype';
import rehypeStringify from 'rehype-stringify';
import { toMarkdown } from 'mdast-util-to-markdown';
import { gfmStrikethroughToMarkdown } from 'mdast-util-gfm-strikethrough';
import type { JSONContent } from '@tiptap/react';

/**
 * Client note markdown ⇄ editor document.
 *
 * The note is stored as markdown because that is what Kyle pastes into a Trello
 * comment. TipTap edits a document, not a string, so this module owns both
 * directions:
 *
 *  - `markdownToHtml` uses the same remark pipeline `AiMarkdown` renders with,
 *    so the editor opens showing exactly what the read view showed.
 *  - `docToMarkdown` walks the ProseMirror document and can only emit the
 *    subset of markdown Trello renders. Anything Trello would show as literal
 *    characters — tables, HTML, colour — has no branch here, so an edit cannot
 *    introduce one.
 */

export function markdownToHtml(markdown: string): string {
  return String(
    unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkBreaks)
      .use(remarkRehype)
      .use(rehypeStringify)
      .processSync(markdown)
  );
}

/* Minimal mdast shapes. Narrower than the real types on purpose: a node kind
   that is missing here is a node kind the note cannot contain. */
type Inline =
  | { type: 'text'; value: string }
  | { type: 'break' }
  | { type: 'inlineCode'; value: string }
  | { type: 'strong'; children: Inline[] }
  | { type: 'emphasis'; children: Inline[] }
  | { type: 'delete'; children: Inline[] }
  | { type: 'link'; url: string; children: Inline[] };

type Block =
  | { type: 'paragraph'; children: Inline[] }
  | { type: 'heading'; depth: 1 | 2 | 3 | 4 | 5 | 6; children: Inline[] }
  | { type: 'blockquote'; children: Block[] }
  | { type: 'list'; ordered: boolean; start?: number; spread: false; children: Block[] }
  | { type: 'listItem'; spread: false; children: Block[] }
  | { type: 'code'; lang: string | null; value: string }
  | { type: 'thematicBreak' };

/* Outermost first: a link wraps its emphasis, never the other way round, so the
   markdown reads `[**text**](url)` and Trello links the whole run. */
const MARK_ORDER = ['link', 'strike', 'bold', 'italic'] as const;

function inlineFrom(nodes: JSONContent[] | undefined): Inline[] {
  const out: Inline[] = [];
  for (const node of nodes ?? []) {
    if (node.type === 'hardBreak') {
      out.push({ type: 'break' });
      continue;
    }
    if (node.type !== 'text' || !node.text) continue;

    const marks = new Map((node.marks ?? []).map((m) => [m.type, m.attrs ?? {}]));
    // `code` is innermost and its content is literal, so it cannot carry the
    // emphasis marks — build it first, then wrap.
    let leaf: Inline = marks.has('code')
      ? { type: 'inlineCode', value: node.text }
      : { type: 'text', value: node.text };

    for (const mark of [...MARK_ORDER].reverse()) {
      if (!marks.has(mark)) continue;
      if (mark === 'link') {
        const href = String(marks.get('link')?.href ?? '');
        if (href) leaf = { type: 'link', url: href, children: [leaf] };
      } else if (mark === 'bold') {
        leaf = { type: 'strong', children: [leaf] };
      } else if (mark === 'italic') {
        leaf = { type: 'emphasis', children: [leaf] };
      } else {
        leaf = { type: 'delete', children: [leaf] };
      }
    }
    out.push(leaf);
  }
  return out;
}

function blocksFrom(nodes: JSONContent[] | undefined): Block[] {
  const out: Block[] = [];
  for (const node of nodes ?? []) {
    switch (node.type) {
      case 'paragraph':
        out.push({ type: 'paragraph', children: inlineFrom(node.content) });
        break;
      case 'heading': {
        const depth = Math.min(6, Math.max(1, Number(node.attrs?.level ?? 3)));
        out.push({ type: 'heading', depth: depth as 1 | 2 | 3 | 4 | 5 | 6, children: inlineFrom(node.content) });
        break;
      }
      case 'blockquote':
        out.push({ type: 'blockquote', children: blocksFrom(node.content) });
        break;
      case 'bulletList':
        out.push({ type: 'list', ordered: false, spread: false, children: blocksFrom(node.content) });
        break;
      case 'orderedList':
        out.push({
          type: 'list',
          ordered: true,
          start: Number(node.attrs?.start ?? 1),
          spread: false,
          children: blocksFrom(node.content),
        });
        break;
      case 'listItem':
        out.push({ type: 'listItem', spread: false, children: blocksFrom(node.content) });
        break;
      case 'codeBlock':
        out.push({
          type: 'code',
          lang: (node.attrs?.language as string) || null,
          value: (node.content ?? []).map((c) => c.text ?? '').join(''),
        });
        break;
      case 'horizontalRule':
        out.push({ type: 'thematicBreak' });
        break;
      default:
        // Unknown block: keep its words rather than dropping them silently.
        if (node.content) out.push({ type: 'paragraph', children: inlineFrom(node.content) });
    }
  }
  return out;
}

export function docToMarkdown(doc: JSONContent): string {
  const tree = { type: 'root' as const, children: blocksFrom(doc.content) };
  const markdown = toMarkdown(tree as never, {
    bullet: '-',
    rule: '-',
    emphasis: '_',
    strong: '*',
    fences: true,
    extensions: [gfmStrikethroughToMarkdown()],
  });
  return markdown.trimEnd();
}

/** Same whitespace split the CLI budgets on, so the count in the editor is the
    count `cockpit ai summary --check` would report. */
export function noteWordCount(markdown: string): number {
  return markdown.trim().split(/\s+/).filter(Boolean).length;
}

export const CLIENT_NOTE_WORD_MAX = 200;
